/**
 * Mutex Global para Eventos de Rastreamento
 * Evita duplicidade de eventos de conversão
 */

// Objeto global para controlar disparos únicos
if (!window.conexchatTrackingMutex) {
  window.conexchatTrackingMutex = {
    lead: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false  // Lock ativo para evitar race conditions
    },
    signup: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    },
    checkout: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    },
    purchase: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    }
  };
}

/**
 * Verifica se um evento já foi disparado ou está processando
 * @param {string} eventType - Tipo do evento (lead, signup, checkout, purchase)
 * @returns {boolean} - true se já foi enviado ou está processando, false caso contrário
 */
export const isEventAlreadySent = (eventType) => {
  const tracking = window.conexchatTrackingMutex;

  if (!tracking || !tracking[eventType]) {
    return false;
  }

  const event = tracking[eventType];

  // PRIMEIRO: Verificar se está processando (lock ativo) - ANTES de verificar sent
  if (event.isProcessing) {
    return true;
  }

  // SEGUNDO: Verificar se já foi enviado
  if (event.sent) {
    return true;
  }

  // TERCEIRO: Verificar timestamp (evitar disparos muito próximos - menos de 2 segundos)
  if (event.timestamp) {
    const now = Date.now();
    const timeDiff = now - event.timestamp;
    if (timeDiff < 2000) {
      return true;
    }
  }

  return false;
};

/**
 * Marca um evento como enviado
 * @param {string} eventType - Tipo do evento
 * @param {string} auid - ID único do evento (retornado pelo Google)
 */
export const markEventAsSent = (eventType, auid = null) => {
  const tracking = window.conexchatTrackingMutex;

  if (!tracking || !tracking[eventType]) {
    return;
  }

  tracking[eventType] = {
    sent: true,
    timestamp: Date.now(),
    auid: auid,
    isProcessing: false
  };
};

/**
 * Reseta todos os eventos (útil para testes)
 */
export const resetTrackingMutex = () => {
  window.conexchatTrackingMutex = {
    lead: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    },
    signup: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    },
    checkout: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    },
    purchase: {
      sent: false,
      timestamp: null,
      auid: null,
      isProcessing: false
    }
  };
};

/**
 * Envia evento de conversão com proteção contra duplicidade
 * @param {string} eventType - Tipo do evento (lead, signup, checkout, purchase)
 * @param {Function} callback - Função que envia o evento
 * @returns {boolean} - true se foi enviado, false se foi ignorado
 */
export const sendTrackingEventOnce = async (eventType, callback) => {
  const tracking = window.conexchatTrackingMutex;

  if (!tracking || !tracking[eventType]) {
    return false;
  }

  const event = tracking[eventType];

  // Verificar se já foi enviado OU está processando
  if (isEventAlreadySent(eventType)) {
    return false;
  }

  // Marcar como processando (lock ativo)
  event.isProcessing = true;

  try {
    // Executar o callback que envia o evento
    const result = await callback();

    // Se sucesso, marcar como enviado
    markEventAsSent(eventType);

    return true;
  } catch (error) {
    console.error(`[MUTEX] Erro ao enviar evento '${eventType}':`, error);
    return false;
  } finally {
    // SEMPRE liberar o lock, mesmo com erro
    event.isProcessing = false;
  }
};

/**
 * Wrapper padronizado para enviar conversões do Google Ads
 * @param {string} eventType - Tipo do evento (lead, signup, checkout, purchase)
 * @param {string} conversionLabel - Label de conversão do Google Ads
 * @param {number} value - Valor da conversão
 * @param {string} currency - Moeda (padrão: BRL)
 * @returns {boolean} - true se foi enviado, false se foi ignorado
 */
export const sendGoogleAdsConversion = async (
  eventType,
  conversionLabel,
  value = 0,
  currency = 'BRL'
) => {
  return sendTrackingEventOnce(eventType, async () => {
    if (!window.gtag) {
      return;
    }

    window.gtag('event', 'conversion', {
      'send_to': `AW-18067457586/${conversionLabel}`,
      'value': value,
      'currency': currency
    });
  });
};

// Exportar para uso no console (debug)
if (typeof window !== 'undefined') {
  window.__CONEXCHAT_TRACKING_DEBUG__ = {
    isEventAlreadySent,
    markEventAsSent,
    resetTrackingMutex,
    getMutex: () => window.conexchatTrackingMutex
  };
}
